import {
  type Access,
  MONEY_MODULES,
  RELEASES,
  type Release,
  seesMoney,
  visibleModules,
} from '@epc/contracts';
import { useSyncExternalStore } from 'react';

/** Последняя просмотренная запись «Что нового» — удобство одного браузера, не важные данные */
const KEY = 'epc:whatsnew-seen';
const EVENT = 'epc:whatsnew-seen';

function read(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function markReleasesSeen() {
  const latest = RELEASES[0]?.id;
  if (!latest) return;
  try {
    localStorage.setItem(KEY, latest);
  } catch {
    // хранилище браузера недоступно — точка «новое» просто останется
  }
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(cb: () => void) {
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', cb);
  };
}

/** Есть ли записи, которые человек ещё не открывал */
export function useHasUnseenReleases(): boolean {
  const seen = useSyncExternalStore(subscribe, read, () => null);
  const latest = RELEASES[0]?.id;
  return Boolean(latest && seen !== latest);
}

/**
 * Записи «Что нового», которые человек может видеть (БЗ-04): общие и о разделах, открытых ему. Запись, где среди
 * разделов есть раздел с деньгами, без уровня «Все деньги» не показывается вовсе — даже если другой её раздел
 * открыт: в тексте такой записи речь о деньгах (как в меню и в инструкции по роли).
 */
export function visibleReleases(access: Access | null | undefined, list: Release[] = RELEASES): Release[] {
  const open = new Set(access ? visibleModules(access).map((m) => m.code) : []);
  const money = access ? seesMoney(access) : false;
  return list.filter(
    (r) =>
      (money || !r.modules.some((m) => MONEY_MODULES.includes(m))) &&
      (!r.modules.length || r.modules.some((m) => open.has(m))),
  );
}

/** Слово для поиска: строчные буквы, «ё» как «е» */
const norm = (s: string) => s.toLowerCase().replaceAll('ё', 'е');

/** Каждое слово запроса есть в тексте (пустой запрос подходит ко всему) */
export function matchesQuery(text: string, query: string): boolean {
  const words = norm(query).split(/\s+/).filter(Boolean);
  const t = norm(text);
  return words.every((w) => t.includes(w));
}

/**
 * Поиск по «Что нового» (БЗ-04, ПМ-11): каждое слово запроса должно найтись в заголовке, пунктах или в пояснении,
 * зачем это сделано (why). Ищет только среди переданных записей — сначала их отбирает visibleReleases.
 */
export function searchReleases(list: Release[], query: string): Release[] {
  if (!query.trim()) return list;
  return list.filter((r) => matchesQuery([r.title, ...r.items, r.why ?? ''].join(' '), query));
}
